import React, { createContext, useContext, useEffect, useState } from 'react';
import { useT } from '../i18n';

const LangContext = createContext();

const SUPPORTED = ['en', 'ru', 'uz'];

// A returning visitor gets the language they picked last; a first-time one gets
// the language their browser asks for, and English only when it asks for
// something we do not speak. Reading the stored value through the list also
// means a stale or hand-edited entry cannot leave the site untranslated.
function initialLang() {
  try {
    const saved = localStorage.getItem('skillup_lang');
    if (SUPPORTED.includes(saved)) return saved;
  } catch { /* private mode: fall through to the browser's preference */ }
  const prefs = navigator.languages || [navigator.language || ''];
  for (const p of prefs) {
    const code = String(p).toLowerCase().split('-')[0];
    if (SUPPORTED.includes(code)) return code;
  }
  return 'en';
}

export function LangProvider({ children }) {
  const [lang, setLang] = useState(initialLang);
  const changeLang = (l) => {
    if (!SUPPORTED.includes(l)) return;
    setLang(l);
    try { localStorage.setItem('skillup_lang', l); } catch { /* nothing to persist to */ }
  };
  // Keep <html lang> in step with the chosen language. Without this the page
  // claims to be English while showing Russian or Uzbek, and Chrome's built-in
  // Google Translate steps in and rewrites the copy — the slogan included.
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const t = useT(lang);
  return <LangContext.Provider value={{ lang, setLang: changeLang, t }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
